import type React from 'react';
import {Box, Button, LinearProgress, Paper, Stack, Typography} from '@mui/material';
import {CheckCircle, RadioButtonUnchecked, Timelapse} from '@mui/icons-material';
import {generatePath, useNavigate, useSearchParams} from 'react-router';
import {routes} from '~/routing/routes';
import type {PlanProps} from '../strategyProps';
import {HelpTitle} from '../SectionLinks';

const QUALITY_HELP = 'Eight checks of a good category strategy, recalculated as the plan changes. Each line says what it '
    + 'needs: requirements on Overview, a roadmap reaching year 3, three team roles, value of 20% of influenceable spend '
    + '(PICK), two non-financial targets, inputs 70% ready, gateway approval and a review cadence. Unmet checks are '
    + 'listed when you submit; they do not block it.';
const INPUTS_HELP = 'The analyses from earlier stages the plan relies on. Most are detected from the data; mark the '
    + 'ones done outside Mithra (Mark done). Open goes to where each input lives.';
const STAGE5_HELP = 'What the plan needs before implementation: gateway approval and action planning are checked '
    + 'automatically; tick business acceptance, change management and the benefits plan when they are in place.';

const ICON = {
    ready: <CheckCircle fontSize="small" color="success"/>,
    partial: <Timelapse fontSize="small" color="warning"/>,
    missing: <RadioButtonUnchecked fontSize="small" color="disabled"/>,
};

/** Strategy quality (8 live checks), input readiness (Stage 1-3 artefacts) and Stage 5 readiness. */
export const ReadinessCards: React.FC<PlanProps & {databagId: number}> = ({plan, actions, databagId}) => {
    const {derived, content, canEdit} = plan;
    const navigate = useNavigate();
    const [params] = useSearchParams();
    const open = (tab: string) => tab === 'explore'
        ? navigate({pathname: generatePath(routes.category_explore, {id: String(databagId)}), search: `?path=${encodeURIComponent(params.get('path') ?? '')}`})
        : navigate({pathname: generatePath(routes.category_profile, {id: String(databagId), tab}), search: params.toString()});
    const mark = (key: string, value: boolean) => void actions.save('inputs', {...content.inputs, [key]: value},
        value ? 'Marked as done.' : 'Mark removed.');
    const stage5 = (key: keyof typeof content.stage5, value: boolean) => void actions.save('stage5',
        {...content.stage5, [key]: value}, value ? 'Marked as done.' : 'Mark removed.');

    return (
        <>
            <Paper variant="outlined" sx={{p: 2}}>
                <Stack direction="row" justifyContent="space-between" alignItems="baseline">
                    <Typography variant="subtitle1" fontWeight={600} component="div">
                        <HelpTitle title="Strategy quality" help={QUALITY_HELP}
                                   links={[{label: 'business requirements on Overview', onClick: () => open('overview')}]}/>
                    </Typography>
                    <Typography variant="body2">{`${derived.qualityMet} of ${derived.quality.length} met`}</Typography>
                </Stack>
                <LinearProgress variant="determinate" value={100 * derived.qualityMet / derived.quality.length} sx={{my: 1}}
                                aria-label="Quality criteria met"/>
                <Stack gap={1}>
                    {derived.quality.map(c => (
                        <Stack key={c.key} direction="row" gap={1} alignItems="flex-start">
                            {c.met ? ICON.ready : ICON.missing}
                            <div>
                                <Typography variant="body2">{c.label}</Typography>
                                <Typography variant="caption" color="text.secondary">{c.evidence}</Typography>
                            </div>
                        </Stack>
                    ))}
                </Stack>
            </Paper>

            <Paper variant="outlined" sx={{p: 2}}>
                <Stack direction="row" justifyContent="space-between" alignItems="baseline">
                    <Typography variant="subtitle1" fontWeight={600} component="div">
                        <HelpTitle title="Input readiness" help={INPUTS_HELP} links={[]}/>
                    </Typography>
                    <Typography variant="body2">{`${derived.inputsPct}%`}</Typography>
                </Stack>
                <LinearProgress variant="determinate" value={derived.inputsPct} sx={{my: 1}} aria-label="Inputs ready"/>
                <Stack gap={0.75}>
                    {derived.inputs.map(i => (
                        <Stack key={i.key} direction="row" gap={1} alignItems="center">
                            {ICON[i.status]}
                            <Typography variant="body2" sx={{flex: 1}}>
                                {i.label}
                            </Typography>
                            {i.markedDone && canEdit && <Button size="small" onClick={() => mark(i.key, false)}>Undo mark</Button>}
                            {!i.markedDone && i.status !== 'ready' && i.canMark && canEdit && (
                                <Button size="small" onClick={() => mark(i.key, true)}>Mark done</Button>
                            )}
                            {i.tab && i.tab !== 'strategy' && i.status !== 'missing' && (
                                <Button size="small" onClick={() => open(i.tab!)}>Open</Button>
                            )}
                        </Stack>
                    ))}
                </Stack>
            </Paper>

            <Paper variant="outlined" sx={{p: 2}}>
                <Typography variant="subtitle1" fontWeight={600} sx={{mb: 1}} component="div">
                    <HelpTitle title="Stage 5 readiness" help={STAGE5_HELP} links={[]}/>
                </Typography>
                <Stack gap={0.75}>
                    {derived.stage5.map(s => (
                        <Stack key={s.key} direction="row" gap={1} alignItems="center">
                            {s.met ? ICON.ready : ICON.missing}
                            <Box sx={{flex: 1}}>
                                <Typography variant="body2">{s.label}</Typography>
                                <Typography variant="caption" color="text.secondary">{s.auto ? 'Automatic' : 'Confirmed by the team'}</Typography>
                            </Box>
                            {!s.auto && canEdit && (
                                <Button size="small" onClick={() => stage5(s.key as keyof typeof content.stage5, !s.met)}>
                                    {s.met ? 'Undo' : 'Mark done'}
                                </Button>
                            )}
                        </Stack>
                    ))}
                </Stack>
            </Paper>
        </>
    );
};
